import { GraphQLClient } from 'graphql-request';
import { DEFAULT_SUBGRAPH_ENDPOINT, DEFAULT_SUBGRAPH_ENDPOINT_TESTNET, DEFAULT_BUILDER_API_URL } from './config.js';
import type { OstiumSubgraphClientConfig } from './config.js';
import { OstiumSubgraphError, OstiumSubgraphErrorCode } from './errors.js';
import { SDK_USER_AGENT } from '../internal/version.js';
import { retryAfterMsFromError, type RetryAfterError } from '../internal/http.js';
import {
  GetPairsAndGroupsQuery,
  GetTraderOpenTradesQuery,
  GetAllOpenTradesQuery,
  GetTraderActiveLimitsQuery,
  GetAccountUpdatesSnapshotQuery,
  buildExecutedFillsQuery,
  buildGetOrdersQuery,
  GetVaultQuery,
} from './queries.js';
import { paginateAll } from './internal/pagination.js';
import {
  chunkArray,
  mergeOrders,
  resolveOrderQueryFlags,
  TRADE_ID_BATCH_SIZE,
  validateOrderFilterParams,
} from './internal/orders.js';
import { getTradePnL, EMPTY_PNL } from './internal/calculations.js';
import {
  dropGhostPendingMarketOrders,
  makePendingOrderRegisteredReader,
  type PendingOrderRegisteredReader,
} from './internal/pending-orders.js';
import { getNetworkConfig } from '../internal/contracts.js';
import { createPublicClient, http } from 'viem';
import { arbitrum, arbitrumSepolia } from 'viem/chains';
import { fetchLivePrices, fetchCandles, OstiumPriceStream } from './internal/pricing.js';
import { OstiumPositionUpdatesStream } from './internal/position-updates.js';
import {
  DEFAULT_POLL_INTERVAL_MS,
  OstiumAccountUpdatesStream,
  extractOrderIdFromReceipt,
  type RawAccountUpdatesSnapshot,
  type TransactionReceiptLike,
} from './internal/account-updates.js';
import { aggregateMarginSummary } from './internal/aggregations.js';
import {
  formatPair,
  formatPosition,
  formatFill,
  formatOrder,
  formatOpenOrder,
  pairMaxLeverage,
} from './internal/formatters.js';
import { formatLeverage } from './internal/decimal.js';
import { simulateSlippageForPair, simulateOrderbookForPair } from './internal/sim-slippage.js';
import { previewOpenTrade as computePreviewOpenTrade, vaultBalanceUsdc } from './internal/preview.js';
import type { RawPair, RawTrade, RawOrder, RawLimit, RawVault } from './internal/raw.js';
import type { Address } from 'viem';
import type {
  Pair,
  PairsResponse,
  AllPricesResponse,
  Position,
  PairPosition,
  OpenPositionsResponse,
  Fill,
  Order,
  OpenOrder,
  PriceData,
  GetPairsParams,
  GetOrdersParams,
  GetBuilderOrdersParams,
  GetUserParams,
  GetOpenPositionsParams,
  GetFillsParams,
  GetFillsByTimeParams,
  GetSimSlippageParams,
  SimSlippageByPairId,
  GetSimOrderbookParams,
  SimOrderbookResponse,
  PreviewOpenTradeParams,
  PreviewOpenTradeResult,
  Candle,
  GetCandlesParams,
  AccountUpdatesForTrader,
  AccountUpdatesSnapshot,
  StreamAccountUpdatesParams,
} from './types.js';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalize an address array into a de-duplicated `Address[]`, preserving the
 * first occurrence of each address (case-insensitive) and dropping empty entries.
 */
function dedupeAddresses(input: Address[]): Address[] {
  const seen = new Set<string>();
  const result: Address[] = [];
  for (const address of input) {
    if (!address) continue;
    const key = address.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(address);
  }
  return result;
}

/**
 * How far back the account snapshot fetches executed close/liquidation orders.
 * Must cover the fast-overlay TTL so any ghost entry can still be tombstoned.
 */
const CLOSED_ORDER_LOOKBACK_MS = 15 * 60 * 1000;
const SHORT_CACHE_TTL_MS = 5_000;
/** Vault balance moves slowly and only gates a limit check — cache it longer. */
const VAULT_CACHE_TTL_MS = 60_000;

interface TimedPromiseCache<T> {
  expiresAt: number;
  promise: Promise<T>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Client
// ─────────────────────────────────────────────────────────────────────────────

export class OstiumSubgraphClient {
  private readonly gql: GraphQLClient;
  private readonly builderApiUrl: string;
  private readonly testnet: boolean;
  private readonly alchemyApiKey?: string;

  /** Cached pair-name (`${from}/${to}`) → `pairId` map. Refreshed lazily. */
  private pairIdCache: Map<string, string> | null = null;
  private pendingOrderReader: PendingOrderRegisteredReader | null = null;
  /** Cached `pairId` → `"FROM-TO"` in raw subgraph dash format (used for OHLC API). */
  private pairRawNameCache: Map<string, string> | null = null;
  /** Cached `pairId` → full raw pair metadata for event overlays. */
  private rawPairCache: Map<string, RawPair> | null = null;
  private rawPairsMemo: TimedPromiseCache<RawPair[]> | null = null;
  private livePricesMemo: TimedPromiseCache<Record<string, PriceData>> | null = null;
  private vaultBalanceMemo: TimedPromiseCache<number> | null = null;

  private constructor(
    gql: GraphQLClient,
    builderApiUrl: string,
    opts: { testnet: boolean; alchemyApiKey?: string },
  ) {
    this.gql = gql;
    this.builderApiUrl = builderApiUrl;
    this.testnet = opts.testnet;
    this.alchemyApiKey = opts.alchemyApiKey;
  }

  /**
   * Create a client connected to the Ostium subgraph.
   *
   * Construction is network-free: the pair-id cache used by `streamPrices`,
   * `getCandles`, and `getAllPrices` is fetched lazily on first use, so
   * build-only consumers (synchronous `get*Tx` encoders) never pay a subgraph
   * round-trip.
   *
   * ```ts
   * // Mainnet (default)
   * const client = await OstiumSubgraphClient.create();
   *
   * // Testnet (Arbitrum Sepolia)
   * const client = await OstiumSubgraphClient.create({ testnet: true });
   * ```
   */
  static async create(config: OstiumSubgraphClientConfig = {}): Promise<OstiumSubgraphClient> {
    const endpoint = config.endpoint
      ?? (config.testnet ? DEFAULT_SUBGRAPH_ENDPOINT_TESTNET : DEFAULT_SUBGRAPH_ENDPOINT);

    if (!endpoint.startsWith('http')) {
      throw new OstiumSubgraphError(
        `Invalid subgraph endpoint: ${endpoint}`,
        OstiumSubgraphErrorCode.INVALID_CONFIG,
      );
    }

    const client = new OstiumSubgraphClient(
      new GraphQLClient(endpoint, {
        headers: { 'User-Agent': SDK_USER_AGENT },
      }),
      config.builderApiUrl ?? DEFAULT_BUILDER_API_URL,
      {
        testnet: config.testnet ?? false,
        alchemyApiKey: config.alchemyApiKey,
      },
    );
    return client;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Pairs
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * All trading pairs with computed `minSz`/`maxBSz`/`maxSSz`, live prices, and
   * market-status flags. Pass `pairIds` to restrict to a subset.
   */
  async getPairs(params?: GetPairsParams): Promise<PairsResponse> {
    const [rawPairs, prices] = await Promise.all([
      this.fetchRawPairs(),
      this.fetchLivePricesSafe(),
    ]);
    this.refreshPairIdCache(rawPairs);

    let filtered = rawPairs;
    if (params?.pairIds && params.pairIds.length > 0) {
      const requested = new Set(params.pairIds.map(String));
      filtered = rawPairs.filter(p => requested.has(String(p.id)));
    }

    return {
      pairs: filtered.map(p =>
        formatPair(p, prices[`${p.from}/${p.to}`], params?.builderFeeBps ?? 0),
      ),
    };
  }

  /** Live mid/bid/ask prices keyed by `pairId`. */
  async getAllPrices(): Promise<AllPricesResponse> {
    const [pairIdMap, prices] = await Promise.all([
      this.ensurePairIdCache(),
      this.fetchLivePricesSafe(),
    ]);

    const out: AllPricesResponse['prices'] = {};
    for (const [name, price] of Object.entries(prices)) {
      const id = pairIdMap.get(name);
      if (!id) continue;
      out[id] = {
        ask: price.ask.toString(),
        bid: price.bid.toString(),
        mid: price.mid.toString(),
      };
    }
    return { prices: out };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Open positions
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Open positions for a trader, sorted newest first, with margin summary.
   *
   * - Fetches live prices automatically.
   * - `blockNumber` is required for live PnL (rollover projection). When omitted,
   *   PnL fields are zeroed.
   */
  async getOpenPositions(params: GetOpenPositionsParams): Promise<OpenPositionsResponse> {
    const { user, blockNumber, limit = Infinity, skip: initialSkip = 0 } = params;
    const isGlobal = user === 'ALL';

    const [trades, prices] = await Promise.all([
      paginateAll<RawTrade>(
        async (skip, first) => {
          if (isGlobal) {
            const data = await this.query<{ trades: RawTrade[] }>(
              GetAllOpenTradesQuery,
              { skip, first },
            );
            return data.trades;
          }
          const data = await this.query<{ trades: RawTrade[] }>(
            GetTraderOpenTradesQuery,
            { trader: user.toLowerCase(), skip, first },
          );
          return data.trades;
        },
        1000,
        limit,
        initialSkip,
      ),
      this.fetchLivePricesSafe(),
    ]);

    const pairPositions: PairPosition[] = trades.map(trade => {
      const price = prices[`${trade.pair.from}/${trade.pair.to}`];
      const pnl   = price && blockNumber ? getTradePnL(trade, price, blockNumber) : EMPTY_PNL;

      const maxLev = trade.isDayTrade ? formatLeverage(trade.pair.overnightMaxLeverage) : pairMaxLeverage(trade.pair);
      return formatPosition(trade, price, pnl, maxLev);
    });

    const marginSummary = aggregateMarginSummary(pairPositions);

    return {
      pairPositions,
      marginSummary,
      time: Date.now(),
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Fills
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Executed fills, sorted by `executedAt desc`.
   *
   * - `user`: pass an address to scope to a single trader, or `'ALL'` to fetch
   *   fills across every trader (no trader filter).
   * - `pairId`: optional pair filter — when set, only fills on this pair are returned.
   */
  async getFills(params: GetFillsParams): Promise<Fill[]> {
    return this.fetchFills(params, undefined);
  }

  /** Executed fills within an inclusive time range (Unix ms). Same filters as `getFills`. */
  async getFillsByTime(params: GetFillsByTimeParams): Promise<Fill[]> {
    const endTime = params.endTime ?? Date.now();
    return this.fetchFills(params, {
      startSec: Math.floor(params.startTime / 1000).toString(),
      endSec:   Math.floor(endTime           / 1000).toString(),
    });
  }

  private async fetchFills(
    filters: GetFillsParams,
    timeRange: { startSec: string; endSec: string } | undefined,
  ): Promise<Fill[]> {
    const hasTrader = filters.user !== 'ALL';
    const hasPairId = filters.pairId !== undefined;
    const limit     = filters.limit ?? 1000;
    const document  = buildExecutedFillsQuery({
      hasTrader,
      hasPairId,
      hasTimeRange: timeRange !== undefined,
    });

    const orders = await paginateAll<RawOrder>(
      async (skip, first) => {
        const variables: Record<string, unknown> = { skip, first };
        if (hasTrader) variables.trader = (filters.user as string).toLowerCase();
        if (hasPairId) variables.pairId = String(filters.pairId);
        if (timeRange) {
          variables.startSec = timeRange.startSec;
          variables.endSec   = timeRange.endSec;
        }
        const data = await this.query<{ orders: RawOrder[] }>(document, variables);
        return data.orders;
      },
      1000,
      limit,
    );
    return orders.map(formatFill);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Open limit orders
  // ─────────────────────────────────────────────────────────────────────────

  /** Active limit orders for a trader, sorted by `updatedAt desc`. */
  async getOpenOrders({ user }: GetUserParams): Promise<OpenOrder[]> {
    const limits = await paginateAll<RawLimit>(async (skip, first) => {
      const data = await this.query<{ limits: RawLimit[] }>(
        GetTraderActiveLimitsQuery,
        { trader: user.toLowerCase(), skip, first },
      );
      return data.limits;
    });
    return limits.map(formatOpenOrder);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Orders (any status)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Fetch orders at any status — pending, executed, or cancelled.
   *
   * - Pass `initiatedTxHashes` (e.g. `[result.txHash]` from `openTrade` / `closeTrade`)
   *   to poll by the subgraph `initiatedTx` field.
   * - Pass `orderIds` to look up by on-chain order id (mutually exclusive with
   *   `initiatedTxHashes`).
   * - Pass `user: 'ALL'` to fetch global orders without a trader filter.
   * - Pass `builder`, `isPending`, `isCanceled`, `pairIds`, or `start` / `end` to filter the result set.
   */
  async getOrders(params: GetOrdersParams = {}): Promise<Order[]> {
    validateOrderFilterParams(params);
    return this.fetchOrders(params, { max: params.limit ?? 100 });
  }

  /**
   * Fetch orders routed through a builder, including sibling close orders on the
   * same positions. The subgraph only stores `builder` on open orders; this method
   * supplements phase-1 builder-tagged orders with phase-2 orders matched by `pid`.
   *
   * `limit` caps phase-1 builder-tagged orders only. Phase-2 sibling orders are
   * appended without a cap, so the merged result may exceed `limit`. Pass `start`
   * and/or `end` to filter both phases by execution time.
   */
  async getBuilderOrders(builder: Address, params: GetBuilderOrdersParams = {}): Promise<Order[]> {
    validateOrderFilterParams(params);
    const normalizedBuilder = builder.toLowerCase();
    const limit = params.limit ?? 100;

    const builderOrders = await this.fetchOrders(
      { ...params, builder: normalizedBuilder },
      { max: limit },
    );
    if (builderOrders.length === 0) return [];

    const pids = [...new Set(builderOrders.map(o => o.pid).filter(Boolean))];
    const oids = builderOrders.map(o => o.oid);

    const siblingOrders = await this.fetchOrdersByTradeIds({
      tradeIds: pids,
      excludeIds: oids,
      filters: params,
    });

    const stampedSiblings = siblingOrders.map(o => ({ ...o, builder: normalizedBuilder }));
    return mergeOrders(builderOrders, stampedSiblings);
  }

  private async fetchOrders(
    params: GetOrdersParams,
    pagination: { max: number },
    extra?: { tradeIds: string[]; excludeIds: string[] },
  ): Promise<Order[]> {
    const flags = resolveOrderQueryFlags(params, extra);
    const document = buildGetOrdersQuery({
      hasOrderIds: flags.hasOrderIds,
      hasInitiatedTxHashes: flags.hasInitiatedTxHashes,
      hasTrader: flags.hasTrader,
      hasBuilder: flags.hasBuilder,
      hasIsPending: flags.hasIsPending,
      hasIsCancelled: flags.hasIsCancelled,
      hasPairIds: flags.hasPairIds,
      hasTradeIds: flags.hasTradeIds,
      hasExcludeIds: flags.hasExcludeIds,
      hasExecutedStart: flags.hasExecutedStart,
      hasExecutedEnd: flags.hasExecutedEnd,
    });

    const orders = await paginateAll<RawOrder>(
      async (skip, first) => {
        const variables = this.buildOrderQueryVariables(params, flags, extra);
        variables.skip = skip;
        variables.first = first;
        const data = await this.query<{ orders: RawOrder[] }>(document, variables);
        return data.orders;
      },
      100,
      pagination.max,
    );

    // Ghost pending market orders (subgraph missed the terminal event, chain
    // has nothing to cancel) are dropped; stale-but-registered ones stay
    // visible so the user cancels them and frees their pending-order slots.
    const live = await dropGhostPendingMarketOrders(orders, this.pendingOrderRegisteredReader());
    return live.map(formatOrder);
  }

  private pendingOrderRegisteredReader(): PendingOrderRegisteredReader {
    if (!this.pendingOrderReader) {
      const chain = this.testnet ? arbitrumSepolia : arbitrum;
      const rpcUrl = this.testnet
        ? 'https://sepolia-rollup.arbitrum.io/rpc'
        : 'https://arb1.arbitrum.io/rpc';
      this.pendingOrderReader = makePendingOrderRegisteredReader(
        createPublicClient({ chain, transport: http(rpcUrl) }),
        getNetworkConfig(this.testnet).contracts.tradingStorage,
      );
    }
    return this.pendingOrderReader;
  }

  private async fetchOrdersByTradeIds(opts: {
    tradeIds: string[];
    excludeIds: string[];
    filters: GetBuilderOrdersParams;
  }): Promise<Order[]> {
    const batches = chunkArray(opts.tradeIds, TRADE_ID_BATCH_SIZE);
    const batchResults: Order[][] = [];

    for (const tradeIds of batches) {
      const batchOrders = await this.fetchOrders(
        { ...opts.filters, limit: undefined },
        { max: Infinity },
        { tradeIds, excludeIds: opts.excludeIds },
      );
      batchResults.push(batchOrders);
    }

    return mergeOrders(...batchResults);
  }

  private buildOrderQueryVariables(
    params: GetOrdersParams,
    flags: ReturnType<typeof resolveOrderQueryFlags>,
    extra?: { tradeIds?: string[]; excludeIds?: string[] },
  ): Record<string, unknown> {
    const variables: Record<string, unknown> = {};
    if (flags.hasOrderIds) variables.ids = params.orderIds!.map(String);
    if (flags.hasInitiatedTxHashes) {
      variables.txHashes = params.initiatedTxHashes!.map(h =>
        typeof h === 'string' ? h.toLowerCase() : h,
      );
    }
    if (flags.hasTrader) variables.trader = (params.user as string).toLowerCase();
    if (flags.hasBuilder) variables.builder = params.builder!.toLowerCase();
    if (flags.hasIsPending) variables.isPending = params.isPending;
    if (flags.hasIsCancelled) variables.isCancelled = flags.isCancelled;
    if (flags.hasPairIds) variables.pairIds = params.pairIds!.map(String);
    if (flags.hasTradeIds) variables.tradeIds = extra!.tradeIds!;
    if (flags.hasExcludeIds) variables.excludeIds = extra!.excludeIds!;
    if (flags.hasExecutedStart) {
      variables.executedStartSec = String(params.start!);
    }
    if (flags.hasExecutedEnd) {
      variables.executedEndSec = String(params.end!);
    }
    return variables;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Simulated slippage
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Simulate the dynamic-spread slippage a long and a short entry of each
   * notional would experience on the given pairs. Notionals exceeding the
   * side's remaining open-interest capacity (`maxOI − sideOI`) are dropped.
   *
   * Computed via `CalculateDynamicPriceImpact` from `@ostium/formulae` using
   * the pair's live OI / decay / threshold params + the current bid/mid/ask
   * from the live-prices feed (collateral = `ntl`, leverage = 1).
   *
   * ```ts
   * const sim = await client.getSimSlippage({
   *   pairIds: ['0', '1'],
   *   ntls:    ['10000', '100000', '1000000'],
   * });
   * // → { '0': { long: [{ ntl, slippage }...], short: [...] }, '1': ... }
   * ```
   */
  async getSimSlippage(params: GetSimSlippageParams): Promise<SimSlippageByPairId> {
    const [rawPairs, prices] = await Promise.all([
      this.fetchRawPairs(),
      this.fetchLivePricesSafe(),
    ]);
    this.refreshPairIdCache(rawPairs);

    const requested = new Set(params.pairIds.map(String));
    const out: SimSlippageByPairId = {};
    for (const pair of rawPairs) {
      const id = String(pair.id);
      if (!requested.has(id)) continue;
      const price = prices[`${pair.from}/${pair.to}`];
      out[id] = simulateSlippageForPair(pair, params.ntls, price);
    }
    return out;
  }

  /**
   * Build a synthetic bid/ask orderbook for a single pair using the on-chain
   * dynamic-spread params. Each side gets up to `levels` (capped at 20)
   * exponentially-spaced notional levels (`[1, 2, 3, 5] × 10ⁿ`), stopping at
   * the side's remaining open-interest capacity.
   *
   * ```ts
   * const ob = await client.getSimOrderbook({ pairId: '0', levels: 20 });
   * // ob.asks: long-entry levels (you'd buy at `px`)
   * // ob.bids: short-entry levels (you'd sell at `px`)
   * ```
   */
  async getSimOrderbook(params: GetSimOrderbookParams): Promise<SimOrderbookResponse> {
    const targetId = String(params.pairId);
    const levels   = params.levels ?? 20;

    const [rawPairs, prices] = await Promise.all([
      this.fetchRawPairs(),
      this.fetchLivePricesSafe(),
    ]);
    this.refreshPairIdCache(rawPairs);

    const pair = rawPairs.find(p => String(p.id) === targetId);
    if (!pair) {
      throw new OstiumSubgraphError(
        `Pair ${targetId} not found in subgraph response`,
        OstiumSubgraphErrorCode.NOT_FOUND,
      );
    }

    const price = prices[`${pair.from}/${pair.to}`];
    if (!price || !price.mid) {
      throw new OstiumSubgraphError(
        `No live price available for ${pair.from}/${pair.to}`,
        OstiumSubgraphErrorCode.NOT_FOUND,
      );
    }

    return simulateOrderbookForPair(pair, price, levels);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Pre-trade preview
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Preview a trade before signing it: execution price after dynamic spread,
   * the fee breakdown, collateral left backing the position, resulting
   * liquidation price, and the validity checks that would otherwise revert
   * on-chain.
   *
   * Every number comes from `@ostium/formulae` — the same math the contracts
   * and ostium.io run — so an order ticket built on this agrees with what the
   * trader sees on the official frontend.
   *
   * ```ts
   * const preview = await client.previewOpenTrade({
   *   pairId: 0, isLong: true, collateral: 100, leverage: 10,
   * });
   *
   * preview.entryPx;          // '95234.12'  — after spread
   * preview.fees.total;       // '0.32'      — deducted from collateral
   * preview.liquidationPx;    // '86104.55'
   * preview.isValid;          // false if `warnings` is non-empty
   * ```
   *
   * Pass `limitPrice` to price a limit/stop order instead of a market order;
   * limit orders execute at their trigger, so they show no dynamic spread.
   */
  async previewOpenTrade(params: PreviewOpenTradeParams): Promise<PreviewOpenTradeResult> {
    const targetId = String(params.pairId);

    // Reject nonsense before three network round-trips. Negative values would
    // otherwise reach the formulas and return a confident, wrong preview with
    // `isValid: true`; non-finite ones throw inside `parseUnits`.
    for (const [name, value] of [
      ['collateral', params.collateral],
      ['leverage', params.leverage],
    ] as const) {
      if (!Number.isFinite(value) || value < 0) {
        throw new OstiumSubgraphError(
          `previewOpenTrade: ${name} must be a non-negative finite number (received ${value})`,
          OstiumSubgraphErrorCode.INVALID_PARAMS,
        );
      }
    }
    if (
      params.limitPrice !== undefined &&
      (!Number.isFinite(params.limitPrice) || params.limitPrice <= 0)
    ) {
      throw new OstiumSubgraphError(
        `previewOpenTrade: limitPrice must be a positive finite number (received ${params.limitPrice})`,
        OstiumSubgraphErrorCode.INVALID_PARAMS,
      );
    }

    const [rawPairs, prices, vaultBalance] = await Promise.all([
      this.fetchRawPairs(),
      this.fetchLivePricesSafe(),
      this.fetchVaultBalanceSafe(),
    ]);
    this.refreshPairIdCache(rawPairs);

    const raw = rawPairs.find(p => String(p.id) === targetId);
    if (!raw) {
      throw new OstiumSubgraphError(
        `Pair ${targetId} not found in subgraph response`,
        OstiumSubgraphErrorCode.NOT_FOUND,
      );
    }

    const price = prices[`${raw.from}/${raw.to}`];
    if (!price || !price.mid) {
      throw new OstiumSubgraphError(
        `No live price available for ${raw.from}/${raw.to}`,
        OstiumSubgraphErrorCode.NOT_FOUND,
      );
    }

    return computePreviewOpenTrade({
      raw,
      price,
      isLong: params.isLong,
      collateral: params.collateral,
      leverage: params.leverage,
      limitPrice: params.limitPrice,
      isDayTrade: params.isDayTrade,
      vaultBalance,
      builderFeeBps: params.builderFeeBps,
    });
  }

  /**
   * Protocol vault balance in USDC. This is the liquidity backing trader PnL,
   * and it caps how much open interest and group collateral a pair can take.
   */
  async getVaultBalance(): Promise<number> {
    const now = Date.now();
    if (this.vaultBalanceMemo && now < this.vaultBalanceMemo.expiresAt) {
      return this.vaultBalanceMemo.promise;
    }

    const promise = this.query<{ vault: RawVault | null }>(GetVaultQuery)
      .then(data => {
        // A missing vault is an unknown balance, not a zero one. Returning 0
        // would make `WithinExposureLimit` reject every non-zero trade; the
        // safe wrapper turns this throw into `withinExposureLimit: undefined`.
        if (!data.vault) {
          throw new OstiumSubgraphError(
            'Vault not found in subgraph response',
            OstiumSubgraphErrorCode.NOT_FOUND,
          );
        }
        return vaultBalanceUsdc(data.vault);
      })
      .catch(error => {
        if (this.vaultBalanceMemo?.promise === promise) this.vaultBalanceMemo = null;
        throw error;
      });

    this.vaultBalanceMemo = { expiresAt: now + VAULT_CACHE_TTL_MS, promise };
    return promise;
  }

  /**
   * `getVaultBalance` that swallows failures — the preview degrades to
   * skipping the exposure-limit check rather than failing outright.
   */
  private async fetchVaultBalanceSafe(): Promise<number | undefined> {
    try {
      return await this.getVaultBalance();
    } catch {
      return undefined;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // OHLC candles
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Fetch OHLC candles for a pair.
   *
   * `pairId` maps to the raw subgraph pair name internally (e.g. pair `0` →
   * `"BTC-USD"`). `from` / `to` are Unix milliseconds; `resolution` is one of
   * `"1"`, `"5"`, `"15"`, `"60"`, `"240"`, `"1D"`. Pass `sets` to fetch
   * multiple candle pages and return them as one flattened array.
   *
   * ```ts
   * const candles = await client.getCandles({
   *   pairId: 0,
   *   from: Date.now() - 7 * 86_400_000,
   *   resolution: '1D',
   *   sets: 3,
   * });
   * ```
   */
  async getCandles(params: GetCandlesParams): Promise<Candle[]> {
    const sets = params.sets ?? 1;
    if (!Number.isInteger(sets) || sets < 1) {
      throw new OstiumSubgraphError(
        'getCandles: sets must be a positive integer',
        OstiumSubgraphErrorCode.INVALID_PARAMS,
      );
    }

    const rawPair = await this.ensurePairRawName(params.pairId);
    return fetchCandles(
      this.builderApiUrl,
      rawPair,
      params.from,
      params.to ?? Date.now(),
      params.resolution,
      sets,
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Live price stream (WebSocket)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Open a WebSocket connection to the live price stream.
   *
   * Optionally filter to a subset of pairs by `pairId` — same values as returned by
   * `getPairs()`. When provided, the client sends `{ type: "subscribe", pairs }` on
   * `open` (no `?pairs=` query on the URL). Omit `pairIds` to receive the full feed.
   *
   * The returned `OstiumPriceStream` fires an initial `snapshot` event with all
   * cached ticks, then a `tick` event on every update. `PriceTick.from` / `.to`
   * are normalized display names (e.g. `"WTI"` not `"CL"`).
   *
   * ```ts
   * const stream = client.streamPrices([0, 1]);  // BTC and ETH by pairId
   *
   * stream.onOpen(() => console.log('connected'));
   * stream.onSnapshot(ticks => console.log('snapshot', ticks.length));
   * stream.onTick(tick => console.log(tick.pair, tick.mid));
   *
   * // Dynamically add / remove by pairId:
   * stream.subscribe([2]);
   * stream.unsubscribe([0]);
   * stream.close();
   * ```
   *
   * Requires a runtime with native `WebSocket` (Node.js 18+, Bun, browsers). Bun’s
   * `WebSocket` may fail some `https://` upgrades (e.g. CloudFront); use Node if so.
  */
  streamPrices(pairIds?: Array<string | number>): OstiumPriceStream {
    const cache = this.pairRawNameCache ?? new Map<string, string>();
    this.pairRawNameCache = cache;

    // Construction no longer prefetches the pair list, so the cache may be
    // cold here. The stream server pushes every pair until a filter is set;
    // connect immediately, warm the cache in the background, and apply the
    // requested filter once ids can resolve to raw names.
    if (pairIds && !this.pairIdCache) {
      const stream = OstiumPriceStream.connect(this.builderApiUrl, undefined, cache);
      void this.ensurePairIdCache()
        .then(() => { stream.subscribe(pairIds); })
        .catch(() => {});
      return stream;
    }

    const rawNames = pairIds?.map(id => {
      const name = cache.get(String(id));
      if (!name) throw new OstiumSubgraphError(`Pair ${id} not found`, OstiumSubgraphErrorCode.NOT_FOUND);
      return name;
    });
    return OstiumPriceStream.connect(this.builderApiUrl, rawNames, cache);
  }

  /**
   * Stream price-driven updates for an existing `getOpenPositions()` response.
   *
   * The SDK subscribes only to the unique pairs present in `initial.pairPositions`,
   * recalculates price-sensitive fields for affected positions on each tick, and
   * emits the full updated response.
   *
   * Pass an existing `priceStream` to reuse a websocket connection your app has
   * already opened. Omit it to let the SDK open and manage a dedicated
   * connection for the tracked pair ids.
   */
  streamPositionUpdates(
    initial: OpenPositionsResponse,
    priceStream?: OstiumPriceStream,
  ): OstiumPositionUpdatesStream {
    const pairIds = [...new Set(initial.pairPositions.map(({ position }) => String(position.pairId)))];
    if (pairIds.length === 0) {
      return new OstiumPositionUpdatesStream(initial, []);
    }
    return new OstiumPositionUpdatesStream(initial, pairIds, priceStream ?? this.streamPrices(pairIds));
  }

  /**
   * Stream low-latency account confirmations.
   *
   * The stream polls the account-level subgraph snapshot for pending market
   * orders, active limit/stop orders, and open trades, then overlays Alchemy
   * contract logs so opens/closes appear before the subgraph catches up.
   */
  streamAccountUpdates(
    params: StreamAccountUpdatesParams & { user: Address[] },
  ): OstiumAccountUpdatesStream {
    const alchemyApiKey = params.alchemyApiKey ?? this.alchemyApiKey;
    if (!alchemyApiKey) {
      throw new OstiumSubgraphError(
        'streamAccountUpdates requires alchemyApiKey in client config or params.',
        OstiumSubgraphErrorCode.INVALID_CONFIG,
      );
    }

    const pollIntervalMs = params.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    if (!Number.isInteger(pollIntervalMs) || pollIntervalMs <= 0) {
      throw new OstiumSubgraphError(
        'streamAccountUpdates pollIntervalMs must be a positive integer.',
        OstiumSubgraphErrorCode.INVALID_CONFIG,
      );
    }

    if (!Array.isArray(params.user)) {
      throw new OstiumSubgraphError(
        'streamAccountUpdates user must be an array of trader addresses.',
        OstiumSubgraphErrorCode.INVALID_PARAMS,
      );
    }

    const users = dedupeAddresses(params.user);
    if (users.length === 0) {
      throw new OstiumSubgraphError(
        'streamAccountUpdates requires at least one trader address.',
        OstiumSubgraphErrorCode.INVALID_PARAMS,
      );
    }

    return new OstiumAccountUpdatesStream({
      users,
      testnet: this.testnet,
      alchemyApiKey,
      pollIntervalMs,
      fetchRawSnapshot: () => this.fetchRawAccountUpdatesSnapshot(users),
      fetchRawPairs: async () => {
        const pairs = await this.fetchRawPairs();
        this.refreshPairIdCache(pairs);
        return pairs;
      },
      fetchLivePrices: () => this.fetchLivePricesSafe({ throwOnError: true }),
      openPriceStream: () => this.streamPrices(),
      getRawPairName: pairId => this.pairRawNameCache?.get(String(pairId)),
      getRawPair: pairId => this.rawPairCache?.get(String(pairId)),
      initialRawPairs: this.rawPairCache ? [...this.rawPairCache.values()] : undefined,
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Internal — fetchers, cache, query wrapper
  // ─────────────────────────────────────────────────────────────────────────

  private async fetchRawPairs(options: { force?: boolean } = {}): Promise<RawPair[]> {
    const now = Date.now();
    if (!options.force && this.rawPairsMemo && now < this.rawPairsMemo.expiresAt) {
      return this.rawPairsMemo.promise;
    }

    const promise = this.query<{ pairs: RawPair[] }>(GetPairsAndGroupsQuery)
      .then(data => {
        this.refreshPairIdCache(data.pairs);
        return data.pairs;
      })
      .catch(error => {
        if (this.rawPairsMemo?.promise === promise) this.rawPairsMemo = null;
        throw error;
      });

    this.rawPairsMemo = {
      expiresAt: now + SHORT_CACHE_TTL_MS,
      promise,
    };
    return promise;
  }

  private async fetchRawAccountUpdatesSnapshot(users: Address[]): Promise<RawAccountUpdatesSnapshot> {
    const first = 1000;
    const traders = users.map(user => user.toLowerCase());
    // Recently executed close/liquidation orders double as tombstones for fast
    // overlay entries whose close event was missed (ghost-position fix).
    const closedSince = Math.floor((Date.now() - CLOSED_ORDER_LOOKBACK_MS) / 1000).toString();

    // One batched query for all traders — `trader_in` filters server-side, so a
    // multi-user stream normally costs a single subgraph request per poll.
    const batched = await this.query<RawAccountUpdatesSnapshot>(
      GetAccountUpdatesSnapshotQuery,
      { traders, first, closedSince },
    );

    // A full page means the shared 1000-row window may have truncated one
    // trader's rows — only then fall back to per-user queries so a busy
    // account cannot starve the others (the pre-0.5 behavior).
    const saturated =
      batched.orders.length >= first ||
      batched.limits.length >= first ||
      batched.trades.length >= first ||
      (batched.closedOrders?.length ?? 0) >= first;
    if (users.length === 1 || !saturated) return batched;

    const snapshots = await Promise.all(
      traders.map(trader =>
        this.query<RawAccountUpdatesSnapshot>(
          GetAccountUpdatesSnapshotQuery,
          { traders: [trader], first, closedSince },
        ),
      ),
    );
    return {
      orders: snapshots.flatMap(snapshot => snapshot.orders),
      limits: snapshots.flatMap(snapshot => snapshot.limits),
      trades: snapshots.flatMap(snapshot => snapshot.trades),
      closedOrders: snapshots.flatMap(snapshot => snapshot.closedOrders ?? []),
    };
  }

  private refreshPairIdCache(rawPairs: RawPair[]): void {
    const pairIdCache = this.pairIdCache ?? new Map<string, string>();
    pairIdCache.clear();
    for (const pair of rawPairs) {
      pairIdCache.set(`${pair.from}/${pair.to}`, String(pair.id));
    }
    this.pairIdCache = pairIdCache;

    const pairRawNameCache = this.pairRawNameCache ?? new Map<string, string>();
    pairRawNameCache.clear();
    for (const pair of rawPairs) {
      pairRawNameCache.set(String(pair.id), `${pair.from}-${pair.to}`);
    }
    this.pairRawNameCache = pairRawNameCache;

    const rawPairCache = this.rawPairCache ?? new Map<string, RawPair>();
    rawPairCache.clear();
    for (const pair of rawPairs) {
      rawPairCache.set(String(pair.id), pair);
    }
    this.rawPairCache = rawPairCache;
  }

  private async ensurePairIdCache(): Promise<Map<string, string>> {
    if (this.pairIdCache) return this.pairIdCache;
    this.refreshPairIdCache(await this.fetchRawPairs());
    return this.pairIdCache!;
  }

  private async ensurePairRawName(pairId: string | number): Promise<string> {
    if (!this.pairRawNameCache) {
      this.refreshPairIdCache(await this.fetchRawPairs());
    }
    const name = this.pairRawNameCache!.get(String(pairId));
    if (!name) {
      throw new OstiumSubgraphError(
        `Pair ${pairId} not found`,
        OstiumSubgraphErrorCode.NOT_FOUND,
      );
    }
    return name;
  }

  private async fetchLivePricesCached(options: { force?: boolean } = {}): Promise<Record<string, PriceData>> {
    const now = Date.now();
    if (!options.force && this.livePricesMemo && now < this.livePricesMemo.expiresAt) {
      return this.livePricesMemo.promise;
    }

    const promise = fetchLivePrices(this.builderApiUrl).catch(error => {
      if (this.livePricesMemo?.promise === promise) this.livePricesMemo = null;
      throw error;
    });

    this.livePricesMemo = {
      expiresAt: now + SHORT_CACHE_TTL_MS,
      promise,
    };
    return promise;
  }

  /** Wrapped `fetchLivePrices` that swallows network errors and returns `{}` by default. */
  private async fetchLivePricesSafe(
    options: { force?: boolean; throwOnError?: boolean } = {},
  ): Promise<Record<string, PriceData>> {
    try {
      return await this.fetchLivePricesCached({ force: options.force });
    } catch (error) {
      if (options.throwOnError) throw error;
      return {};
    }
  }

  private async query<T>(document: string, variables?: Record<string, unknown>): Promise<T> {
    try {
      return await this.gql.request<T>(document, variables);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const lower = msg.toLowerCase();

      const retryAfterMs = retryAfterMsFromError(err);

      if (lower.includes('network') || lower.includes('timeout') || lower.includes('fetch')) {
        const wrapped = new OstiumSubgraphError(
          `Subgraph network error: ${msg}`,
          OstiumSubgraphErrorCode.FETCH_FAILED,
          err,
        );
        if (retryAfterMs !== undefined) (wrapped as RetryAfterError).retryAfterMs = retryAfterMs;
        throw wrapped;
      }

      const wrapped = new OstiumSubgraphError(
        `Subgraph query failed: ${msg}`,
        OstiumSubgraphErrorCode.FETCH_FAILED,
        err,
      );
      if (retryAfterMs !== undefined) (wrapped as RetryAfterError).retryAfterMs = retryAfterMs;
      throw wrapped;
    }
  }
}

// Silence unused-import warning when consumers re-export.
export { OstiumPositionUpdatesStream, OstiumAccountUpdatesStream, extractOrderIdFromReceipt };
export type {
  Position,
  AccountUpdatesForTrader,
  AccountUpdatesSnapshot,
  StreamAccountUpdatesParams,
  TransactionReceiptLike,
};
